"use strict";

exports.__esModule = true;
exports.Skeleton = void 0;

var _MeshAttachment = require("./attachments/MeshAttachment");

var _PathAttachment = require("./attachments/PathAttachment");

var _RegionAttachment = require("./attachments/RegionAttachment");

var _Bone = require("./Bone");

var _IkConstraint = require("./IkConstraint");

var _PathConstraint = require("./PathConstraint");

var _Slot = require("./Slot");

var _TransformConstraint = require("./TransformConstraint");

var _Utils = require("./Utils");

/******************************************************************************
 * Spine Runtimes License Agreement
 * Last updated January 1, 2020. Replaces all prior versions.
 *
 * Copyright (c) 2013-2020, Esoteric Software LLC
 *
 * Integration of the Spine Runtimes into software or otherwise creating
 * derivative works of the Spine Runtimes is permitted under the terms and
 * conditions of Section 2 of the Spine Editor License Agreement:
 * http://esotericsoftware.com/spine-editor-license
 *
 * Otherwise, it is permitted to integrate the Spine Runtimes into software
 * or otherwise create derivative works of the Spine Runtimes (collectively,
 * "Products"), provided that each user of the Products must obtain their own
 * Spine Editor license and redistribution of the Products in any form must
 * include this license and copyright notice.
 *
 * THE SPINE RUNTIMES ARE PROVIDED BY ESOTERIC SOFTWARE LLC "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL ESOTERIC SOFTWARE LLC BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
 * BUSINESS INTERRUPTION, OR LOSS OF USE, DATA, OR PROFITS) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THE SPINE RUNTIMES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *****************************************************************************/

/** Stores the current pose for a skeleton.
 *
 * See [Instance objects](http://esotericsoftware.com/spine-runtime-architecture#Instance-objects) in the Spine Runtimes Guide. */
var Skeleton = /*#__PURE__*/function () {
  function Skeleton(data) {
    /** The skeleton's setup pose data. */
    this.data = null;
    /** The skeleton's bones, sorted parent first. The root bone is always the first bone. */

    this.bones = null;
    /** The skeleton's slots. */

    this.slots = null;
    /** The skeleton's slots in the order they should be drawn. The returned array may be modified to change the draw order. */

    this.drawOrder = null;
    /** The skeleton's IK constraints. */

    this.ikConstraints = null;
    /** The skeleton's transform constraints. */

    this.transformConstraints = null;
    /** The skeleton's path constraints. */

    this.pathConstraints = null;
    /** The list of bones and constraints, sorted in the order they should be updated, as computed by {@link #updateCache()}. */

    this._updateCache = new Array();
    /** The skeleton's current skin. May be null. */

    this.skin = null;
    /** The color to tint all the skeleton's attachments. */

    this.color = null;
    /** Returns the skeleton's time. This can be used for tracking, such as with Slot {@link Slot#attachmentTime}.
     * <p>
     * See {@link #update()}. */

    this.time = 0;
    /** Scales the entire skeleton on the X axis. This affects all bones, even if the bone's transform mode disallows scale
      * inheritance. */

    this.scaleX = 1;
    /** Scales the entire skeleton on the Y axis. This affects all bones, even if the bone's transform mode disallows scale
      * inheritance. */

    this.scaleY = 1;
    /** Sets the skeleton X position, which is added to the root bone worldX position. */

    this.x = 0;
    /** Sets the skeleton Y position, which is added to the root bone worldY position. */

    this.y = 0;
    if (!data) throw new Error("data cannot be null.");
    this.data = data;
    this.bones = new Array();

    for (var i = 0; i < data.bones.length; i++) {
      var boneData = data.bones[i];
      var bone = void 0;
      if (!boneData.parent) bone = new _Bone.Bone(boneData, this, null);else {
        var parent = this.bones[boneData.parent.index];
        bone = new _Bone.Bone(boneData, this, parent);
        parent.children.push(bone);
      }
      this.bones.push(bone);
    }

    this.slots = new Array();
    this.drawOrder = new Array();

    for (var _i = 0; _i < data.slots.length; _i++) {
      var slotData = data.slots[_i];
      var _bone = this.bones[slotData.boneData.index];
      var slot = new _Slot.Slot(slotData, _bone);
      this.slots.push(slot);
      this.drawOrder.push(slot);
    }

    this.ikConstraints = new Array();

    for (var _i2 = 0; _i2 < data.ikConstraints.length; _i2++) {
      var ikConstraintData = data.ikConstraints[_i2];
      this.ikConstraints.push(new _IkConstraint.IkConstraint(ikConstraintData, this));
    }

    this.transformConstraints = new Array();

    for (var _i3 = 0; _i3 < data.transformConstraints.length; _i3++) {
      var transformConstraintData = data.transformConstraints[_i3];
      this.transformConstraints.push(new _TransformConstraint.TransformConstraint(transformConstraintData, this));
    }

    this.pathConstraints = new Array();

    for (var _i4 = 0; _i4 < data.pathConstraints.length; _i4++) {
      var pathConstraintData = data.pathConstraints[_i4];
      this.pathConstraints.push(new _PathConstraint.PathConstraint(pathConstraintData, this));
    }

    this.color = new _Utils.Color(1, 1, 1, 1);
    this.updateCache();
  }
  /** Caches information about bones and constraints. Must be called if the {@link #getSkin()} is modified or if bones,
   * constraints, or weighted path attachments are added or removed. */


  var _proto = Skeleton.prototype;

  _proto.updateCache = function updateCache() {
    var updateCache = this._updateCache;
    updateCache.length = 0;
    var bones = this.bones;

    for (var i = 0, n = bones.length; i < n; i++) {
      var bone = bones[i];
      bone.sorted = bone.data.skinRequired;
      bone.active = !bone.sorted;
    }

    if (this.skin) {
      var skinBones = this.skin.bones;

      for (var _i5 = 0, _n = this.skin.bones.length; _i5 < _n; _i5++) {
        var _bone2 = this.bones[skinBones[_i5].index];

        do {
          _bone2.sorted = false;
          _bone2.active = true;
          _bone2 = _bone2.parent;
        } while (_bone2);
      }
    } // IK first, lowest hierarchy depth first.


    var ikConstraints = this.ikConstraints;
    var transformConstraints = this.transformConstraints;
    var pathConstraints = this.pathConstraints;
    var ikCount = ikConstraints.length,
        transformCount = transformConstraints.length,
        pathCount = pathConstraints.length;
    var constraintCount = ikCount + transformCount + pathCount;

    outer: for (var _i6 = 0; _i6 < constraintCount; _i6++) {
      for (var ii = 0; ii < ikCount; ii++) {
        var constraint = ikConstraints[ii];

        if (constraint.data.order == _i6) {
          this.sortIkConstraint(constraint);
          continue outer;
        }
      }

      for (var _ii = 0; _ii < transformCount; _ii++) {
        var _constraint = transformConstraints[_ii];

        if (_constraint.data.order == _i6) {
          this.sortTransformConstraint(_constraint);
          continue outer;
        }
      }

      for (var _ii2 = 0; _ii2 < pathCount; _ii2++) {
        var _constraint2 = pathConstraints[_ii2];

        if (_constraint2.data.order == _i6) {
          this.sortPathConstraint(_constraint2);
          continue outer;
        }
      }
    }

    for (var _i7 = 0, _n2 = bones.length; _i7 < _n2; _i7++) {
      this.sortBone(bones[_i7]);
    }
  };

  _proto.sortIkConstraint = function sortIkConstraint(constraint) {
    constraint.active = constraint.target.isActive() && (!constraint.data.skinRequired || this.skin && _Utils.Utils.contains(this.skin.constraints, constraint.data, true));
    if (!constraint.active) return;
    var target = constraint.target;
    this.sortBone(target);
    var constrained = constraint.bones;
    var parent = constrained[0];
    this.sortBone(parent);

    if (constrained.length == 1) {
      this._updateCache.push(constraint);

      this.sortReset(parent.children);
    } else {
      var child = constrained[constrained.length - 1];
      this.sortBone(child);

      this._updateCache.push(constraint);

      this.sortReset(parent.children);
      child.sorted = true;
    }
  };

  _proto.sortPathConstraint = function sortPathConstraint(constraint) {
    constraint.active = constraint.target.bone.isActive() && (!constraint.data.skinRequired || this.skin && _Utils.Utils.contains(this.skin.constraints, constraint.data, true));
    if (!constraint.active) return;
    var slot = constraint.target;
    var slotIndex = slot.data.index;
    var slotBone = slot.bone;
    if (this.skin) this.sortPathConstraintAttachment(this.skin, slotIndex, slotBone);
    if (this.data.defaultSkin && this.data.defaultSkin != this.skin) this.sortPathConstraintAttachment(this.data.defaultSkin, slotIndex, slotBone);

    for (var i = 0, n = this.data.skins.length; i < n; i++) {
      this.sortPathConstraintAttachment(this.data.skins[i], slotIndex, slotBone);
    }

    var attachment = slot.getAttachment();
    if (attachment instanceof _PathAttachment.PathAttachment) this.sortPathConstraintAttachmentWith(attachment, slotBone);
    var constrained = constraint.bones;
    var boneCount = constrained.length;

    for (var _i8 = 0; _i8 < boneCount; _i8++) {
      this.sortBone(constrained[_i8]);
    }

    this._updateCache.push(constraint);

    for (var _i9 = 0; _i9 < boneCount; _i9++) {
      this.sortReset(constrained[_i9].children);
    }

    for (var _i10 = 0; _i10 < boneCount; _i10++) {
      constrained[_i10].sorted = true;
    }
  };

  _proto.sortTransformConstraint = function sortTransformConstraint(constraint) {
    constraint.active = constraint.target.isActive() && (!constraint.data.skinRequired || this.skin && _Utils.Utils.contains(this.skin.constraints, constraint.data, true));
    if (!constraint.active) return;
    this.sortBone(constraint.target);
    var constrained = constraint.bones;
    var boneCount = constrained.length;

    if (constraint.data.local) {
      for (var i = 0; i < boneCount; i++) {
        var child = constrained[i];
        this.sortBone(child.parent);
        this.sortBone(child);
      }
    } else {
      for (var _i11 = 0; _i11 < boneCount; _i11++) {
        this.sortBone(constrained[_i11]);
      }
    }

    this._updateCache.push(constraint);

    for (var _i12 = 0; _i12 < boneCount; _i12++) {
      this.sortReset(constrained[_i12].children);
    }

    for (var _i13 = 0; _i13 < boneCount; _i13++) {
      constrained[_i13].sorted = true;
    }
  };

  _proto.sortPathConstraintAttachment = function sortPathConstraintAttachment(skin, slotIndex, slotBone) {
    var attachments = skin.attachments[slotIndex];
    if (!attachments) return;

    for (var key in attachments) {
      this.sortPathConstraintAttachmentWith(attachments[key], slotBone);
    }
  };

  _proto.sortPathConstraintAttachmentWith = function sortPathConstraintAttachmentWith(attachment, slotBone) {
    if (!(attachment instanceof _PathAttachment.PathAttachment)) return;
    var pathBones = attachment.bones;
    if (!pathBones) this.sortBone(slotBone);else {
      var bones = this.bones;

      for (var i = 0, n = pathBones.length; i < n;) {
        var nn = pathBones[i++];
        nn += i;

        while (i < nn) {
          this.sortBone(bones[pathBones[i++]]);
        }
      }
    }
  };

  _proto.sortBone = function sortBone(bone) {
    if (bone.sorted) return;
    var parent = bone.parent;
    if (parent) this.sortBone(parent);
    bone.sorted = true;

    this._updateCache.push(bone);
  };

  _proto.sortReset = function sortReset(bones) {
    for (var i = 0, n = bones.length; i < n; i++) {
      var bone = bones[i];
      if (!bone.active) continue;
      if (bone.sorted) this.sortReset(bone.children);
      bone.sorted = false;
    }
  }
  /** Updates the world transform for each bone and applies all constraints.
   *
   * See [World transforms](http://esotericsoftware.com/spine-runtime-skeletons#World-transforms) in the Spine
   * Runtimes Guide. */
  ;

  _proto.updateWorldTransform = function updateWorldTransform() {
    var bones = this.bones;

    for (var i = 0, n = bones.length; i < n; i++) {
      var bone = bones[i];
      bone.ax = bone.x;
      bone.ay = bone.y;
      bone.arotation = bone.rotation;
      bone.ascaleX = bone.scaleX;
      bone.ascaleY = bone.scaleY;
      bone.ashearX = bone.shearX;
      bone.ashearY = bone.shearY;
    }

    var updateCache = this._updateCache;

    for (var _i14 = 0, _n3 = updateCache.length; _i14 < _n3; _i14++) {
      updateCache[_i14].update();
    }
  };

  _proto.updateWorldTransformWith = function updateWorldTransformWith(parent) {
    // Apply the parent bone transform to the root bone. The root bone always inherits scale, rotation and reflection.
    var rootBone = this.getRootBone();
    var pa = parent.a,
        pb = parent.b,
        pc = parent.c,
        pd = parent.d;
    rootBone.worldX = pa * this.x + pb * this.y + parent.worldX;
    rootBone.worldY = pc * this.x + pd * this.y + parent.worldY;
    var rotationY = rootBone.rotation + 90 + rootBone.shearY;
    var la = _Utils.MathUtils.cosDeg(rootBone.rotation + rootBone.shearX) * rootBone.scaleX;
    var lb = _Utils.MathUtils.cosDeg(rotationY) * rootBone.scaleY;
    var lc = _Utils.MathUtils.sinDeg(rootBone.rotation + rootBone.shearX) * rootBone.scaleX;
    var ld = _Utils.MathUtils.sinDeg(rotationY) * rootBone.scaleY;
    rootBone.a = (pa * la + pb * lc) * this.scaleX;
    rootBone.b = (pa * lb + pb * ld) * this.scaleX;
    rootBone.c = (pc * la + pd * lc) * this.scaleY;
    rootBone.d = (pc * lb + pd * ld) * this.scaleY; // Update everything except root bone.

    var updateCache = this._updateCache;

    for (var i = 0, n = updateCache.length; i < n; i++) {
      var updatable = updateCache[i];
      if (updatable != rootBone) updatable.update();
    }
  }
  /** Sets the bones, constraints, and slots to their setup pose values. */
  ;

  _proto.setToSetupPose = function setToSetupPose() {
    this.setBonesToSetupPose();
    this.setSlotsToSetupPose();
  }
  /** Sets the bones and constraints to their setup pose values. */
  ;

  _proto.setBonesToSetupPose = function setBonesToSetupPose() {
    var bones = this.bones;

    for (var i = 0, n = bones.length; i < n; i++) {
      bones[i].setToSetupPose();
    }

    var ikConstraints = this.ikConstraints;

    for (var _i15 = 0, _n4 = ikConstraints.length; _i15 < _n4; _i15++) {
      var constraint = ikConstraints[_i15];
      constraint.mix = constraint.data.mix;
      constraint.softness = constraint.data.softness;
      constraint.bendDirection = constraint.data.bendDirection;
      constraint.compress = constraint.data.compress;
      constraint.stretch = constraint.data.stretch;
    }

    var transformConstraints = this.transformConstraints;

    for (var _i16 = 0, _n5 = transformConstraints.length; _i16 < _n5; _i16++) {
      var _constraint3 = transformConstraints[_i16];
      var data = _constraint3.data;
      _constraint3.mixRotate = data.mixRotate;
      _constraint3.mixX = data.mixX;
      _constraint3.mixY = data.mixY;
      _constraint3.mixScaleX = data.mixScaleX;
      _constraint3.mixScaleY = data.mixScaleY;
      _constraint3.mixShearY = data.mixShearY;
    }

    var pathConstraints = this.pathConstraints;

    for (var _i17 = 0, _n6 = pathConstraints.length; _i17 < _n6; _i17++) {
      var _constraint4 = pathConstraints[_i17];
      var _data = _constraint4.data;
      _constraint4.position = _data.position;
      _constraint4.spacing = _data.spacing;
      _constraint4.mixRotate = _data.mixRotate;
      _constraint4.mixX = _data.mixX;
      _constraint4.mixY = _data.mixY;
    }
  }
  /** Sets the slots and draw order to their setup pose values. */
  ;

  _proto.setSlotsToSetupPose = function setSlotsToSetupPose() {
    var slots = this.slots;

    _Utils.Utils.arrayCopy(slots, 0, this.drawOrder, 0, slots.length);

    for (var i = 0, n = slots.length; i < n; i++) {
      slots[i].setToSetupPose();
    }
  }
  /** @returns May return null. */
  ;

  _proto.getRootBone = function getRootBone() {
    if (this.bones.length == 0) return null;
    return this.bones[0];
  }
  /** @returns May be null. */
  ;

  _proto.findBone = function findBone(boneName) {
    if (!boneName) throw new Error("boneName cannot be null.");
    var bones = this.bones;

    for (var i = 0, n = bones.length; i < n; i++) {
      var bone = bones[i];
      if (bone.data.name == boneName) return bone;
    }

    return null;
  }
  /** Finds a slot by comparing each slot's name. It is more efficient to cache the results of this method than to call it
   * repeatedly.
   * @returns May be null. */
  ;

  _proto.findSlot = function findSlot(slotName) {
    if (!slotName) throw new Error("slotName cannot be null.");
    var slots = this.slots;

    for (var i = 0, n = slots.length; i < n; i++) {
      var slot = slots[i];
      if (slot.data.name == slotName) return slot;
    }

    return null;
  }
  /** Sets a skin by name.
   *
   * See {@link #setSkin()}. */
  ;

  _proto.setSkinByName = function setSkinByName(skinName) {
    var skin = this.data.findSkin(skinName);
    if (!skin) throw new Error("Skin not found: " + skinName);
    this.setSkin(skin);
  }
  /** Sets the skin used to look up attachments before looking in the {@link SkeletonData#defaultSkin default skin}. If the
   * skin is changed, {@link #updateCache()} is called.
   *
   * Attachments from the new skin are attached if the corresponding attachment from the old skin was attached. If there was no
   * old skin, each slot's setup mode attachment is attached from the new skin.
   *
   * After changing the skin, the visible attachments can be reset to those attached in the setup pose by calling
   * {@link #setSlotsToSetupPose()}. Also, often {@link AnimationState#apply()} is called before the next time the
   * skeleton is rendered to allow any attachment keys in the current animation(s) to hide or show attachments from the new skin.
   * @param newSkin May be null. */
  ;

  _proto.setSkin = function setSkin(newSkin) {
    if (newSkin == this.skin) return;

    if (newSkin) {
      if (this.skin) newSkin.attachAll(this, this.skin);else {
        var slots = this.slots;

        for (var i = 0, n = slots.length; i < n; i++) {
          var slot = slots[i];
          var name = slot.data.attachmentName;

          if (name) {
            var attachment = newSkin.getAttachment(i, name);
            if (attachment) slot.setAttachment(attachment);
          }
        }
      }
    }

    this.skin = newSkin;
    this.updateCache();
  }
  /** Finds an attachment by looking in the {@link #skin} and {@link SkeletonData#defaultSkin} using the slot name and attachment
   * name.
   *
   * See {@link #getAttachment()}.
   * @returns May be null. */
  ;

  _proto.getAttachmentByName = function getAttachmentByName(slotName, attachmentName) {
    return this.getAttachment(this.data.findSlot(slotName).index, attachmentName);
  }
  /** Finds an attachment by looking in the {@link #skin} and {@link SkeletonData#defaultSkin} using the slot index and
   * attachment name. First the skin is checked and if the attachment was not found, the default skin is checked.
   *
   * See [Runtime skins](http://esotericsoftware.com/spine-runtime-skins) in the Spine Runtimes Guide.
   * @returns May be null. */
  ;

  _proto.getAttachment = function getAttachment(slotIndex, attachmentName) {
    if (!attachmentName) throw new Error("attachmentName cannot be null.");

    if (this.skin) {
      var attachment = this.skin.getAttachment(slotIndex, attachmentName);
      if (attachment) return attachment;
    }

    if (this.data.defaultSkin) return this.data.defaultSkin.getAttachment(slotIndex, attachmentName);
    return null;
  }
  /** A convenience method to set an attachment by finding the slot with {@link #findSlot()}, finding the attachment with
   * {@link #getAttachment()}, then setting the slot's {@link Slot#attachment}.
   * @param attachmentName May be null to clear the slot's attachment. */
  ;

  _proto.setAttachment = function setAttachment(slotName, attachmentName) {
    if (!slotName) throw new Error("slotName cannot be null.");
    var slots = this.slots;

    for (var i = 0, n = slots.length; i < n; i++) {
      var slot = slots[i];

      if (slot.data.name == slotName) {
        var attachment = null;

        if (attachmentName) {
          attachment = this.getAttachment(i, attachmentName);
          if (!attachment) throw new Error("Attachment not found: " + attachmentName + ", for slot: " + slotName);
        }

        slot.setAttachment(attachment);
        return;
      }
    }

    throw new Error("Slot not found: " + slotName);
  }
  /** Finds an IK constraint by comparing each IK constraint's name. It is more efficient to cache the results of this method
   * than to call it repeatedly.
   * @return May be null. */
  ;

  _proto.findIkConstraint = function findIkConstraint(constraintName) {
    if (!constraintName) throw new Error("constraintName cannot be null.");
    var ikConstraints = this.ikConstraints;

    for (var i = 0, n = ikConstraints.length; i < n; i++) {
      var ikConstraint = ikConstraints[i];
      if (ikConstraint.data.name == constraintName) return ikConstraint;
    }

    return null;
  }
  /** Finds a transform constraint by comparing each transform constraint's name. It is more efficient to cache the results of
   * this method than to call it repeatedly.
   * @return May be null. */
  ;

  _proto.findTransformConstraint = function findTransformConstraint(constraintName) {
    if (!constraintName) throw new Error("constraintName cannot be null.");
    var transformConstraints = this.transformConstraints;

    for (var i = 0, n = transformConstraints.length; i < n; i++) {
      var constraint = transformConstraints[i];
      if (constraint.data.name == constraintName) return constraint;
    }

    return null;
  }
  /** Finds a path constraint by comparing each path constraint's name. It is more efficient to cache the results of this method
   * than to call it repeatedly.
   * @return May be null. */
  ;

  _proto.findPathConstraint = function findPathConstraint(constraintName) {
    if (!constraintName) throw new Error("constraintName cannot be null.");
    var pathConstraints = this.pathConstraints;

    for (var i = 0, n = pathConstraints.length; i < n; i++) {
      var constraint = pathConstraints[i];
      if (constraint.data.name == constraintName) return constraint;
    }

    return null;
  }
  /** Returns the axis aligned bounding box (AABB) of the region and mesh attachments for the current pose as `{ x: number, y: number, width: number, height: number }`.
   * Note that this method will create temporary objects which can add to garbage collection pressure. Use `getBounds()` if garbage collection is a concern. */
  ;

  _proto.getBoundsRect = function getBoundsRect() {
    var offset = new _Utils.Vector2();
    var size = new _Utils.Vector2();
    this.getBounds(offset, size);
    return {
      x: offset.x,
      y: offset.y,
      width: size.x,
      height: size.y
    };
  }
  /** Returns the axis aligned bounding box (AABB) of the region and mesh attachments for the current pose.
   * @param offset An output value, the distance from the skeleton origin to the bottom left corner of the AABB.
   * @param size An output value, the width and height of the AABB.
   * @param temp Working memory to temporarily store attachments' computed world vertices. */
  ;

  _proto.getBounds = function getBounds(offset, size, temp) {
    if (temp === void 0) {
      temp = new Array(2);
    }

    if (!offset) throw new Error("offset cannot be null.");
    if (!size) throw new Error("size cannot be null.");
    var drawOrder = this.drawOrder;
    var minX = Number.POSITIVE_INFINITY,
        minY = Number.POSITIVE_INFINITY,
        maxX = Number.NEGATIVE_INFINITY,
        maxY = Number.NEGATIVE_INFINITY;

    for (var i = 0, n = drawOrder.length; i < n; i++) {
      var slot = drawOrder[i];
      if (!slot.bone.active) continue;
      var verticesLength = 0;
      var vertices = null;
      var attachment = slot.getAttachment();

      if (attachment instanceof _RegionAttachment.RegionAttachment) {
        verticesLength = 8;
        vertices = _Utils.Utils.setArraySize(temp, verticesLength, 0);
        attachment.computeWorldVertices(slot.bone, vertices, 0, 2);
      } else if (attachment instanceof _MeshAttachment.MeshAttachment) {
        var mesh = attachment;
        verticesLength = mesh.worldVerticesLength;
        vertices = _Utils.Utils.setArraySize(temp, verticesLength, 0);
        mesh.computeWorldVertices(slot, 0, verticesLength, vertices, 0, 2);
      }

      if (vertices) {
        for (var ii = 0, nn = vertices.length; ii < nn; ii += 2) {
          var x = vertices[ii],
              y = vertices[ii + 1];
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    offset.set(minX, minY);
    size.set(maxX - minX, maxY - minY);
  }
  /** Increments the skeleton's {@link #time}. */
  ;

  _proto.update = function update(delta) {
    this.time += delta;
  };

  return Skeleton;
}();

exports.Skeleton = Skeleton;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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